import { createStore, createEvent, createEffect, sample } from 'effector';
import { AuthenticatedRequest, createAuthenticatedRequest } from './types';
import { createTelegramApiClient } from '../utils/apiClient';
import { MainButtonStore, setMainButton } from './mainButtonStore';
import { scanQRCode, activatePendingCode } from './userStore';
import { $skipQrScan } from './skipQrScan';
import { SecondaryButtonState, setSecondaryButtonState } from './secondButtonStore';

// Типы

export interface ActivationRequest {
  code: string;
  telegramId: string;
  username: string;
  phone: string;
  displayName: string;
}

export interface RequestResponse {
  success: boolean;
  message?: string;
  rewardId?: number;
  errorCode?: string;
  timestamp?: string;
}

export interface ErrorResponse {
  message: string;
  errorCode?: string;
  isSessionExpired?: boolean;
}

// События
export const setQrCode = createEvent<string>();
export const setMessage = createEvent<string>();
export const setAnnotation = createEvent<string | undefined>();
export const setIsLoading = createEvent<boolean>();
export const setShowScanner = createEvent<boolean>();
export const setSessionExpired = createEvent<boolean>();
export const setShowRewardCongratulations = createEvent<boolean>();
export const resetActivation = createEvent();

// Эффекты

export const  activateQrCodeFx= createEffect<AuthenticatedRequest<ActivationRequest>, RequestResponse, ErrorResponse>(async ({ data, initData }) => {
  console.log('activateQrCodeFx started with data:', data);
  
  try {
    const telegramClient = createTelegramApiClient(initData);
    const result = await telegramClient.post('/activation', data, {
      loadingText: 'Активируем QR-код...'
    });
    
    console.log('Response result:', result);
    return result;
  } catch (error: any) {
    console.log('Error details:', error);
    
    // Проверяем, является ли ошибка связанной с истекшим сеансом
    const isSessionExpired = error.message?.toLowerCase().includes('сеанс истек') ||
                            error.message?.toLowerCase().includes('session expired') ||
                            error.message?.toLowerCase().includes('401');
    
    console.log('Error detected, isSessionExpired:', isSessionExpired);

    throw Object.assign(new Error(error.message || 'Ошибка активации QR-кода'), {
      errorCode: error.errorCode,
      isSessionExpired
    });
  }
});

// Тип состояния
export interface ActivationState {
  qrCode: string;
  message: string;
  annotation?: string;
  rewardId: number | null;
  success: boolean | null;
  isLoading: boolean;
  showScanner: boolean;
  telegramInitData: string;
  isSessionExpired: boolean;
  showRewardCongratulations: boolean;
}

// Store
export const activationStore = createStore<ActivationState>({
  qrCode: '',
  message: '',
  rewardId: null,
  success: null,
  isLoading: false,
  showScanner: false,
  telegramInitData: '',
  isSessionExpired: false,
  showRewardCongratulations: false,
})
  .on(setQrCode, (state, qrCode) => ({ ...state, qrCode }))
  .on(setMessage, (state, message) => ({ ...state, message, annotation: '' }))
  .on(setAnnotation, (state, annotation) => {
    console.log('setAnnotation called with:', annotation);
    return { ...state, annotation };
  })
  .on(setIsLoading, (state, isLoading) => ({ ...state, isLoading }))
  .on(setShowScanner, (state, showScanner) => ({ ...state, showScanner }))
  .on(setSessionExpired, (state, isSessionExpired) => ({ ...state, isSessionExpired }))
  .on(setShowRewardCongratulations, (state, showRewardCongratulations) => ({ ...state, showRewardCongratulations }))
  .on(resetActivation, (state) => {
    return { ...state, 
      qrCode: '', 
      message: '', 
      annotation: undefined,
      rewardId: null, 
      success: null, 
      isLoading: false, 
      showScanner: false, 
      isSessionExpired: false, 
      showRewardCongratulations: false 
    } as ActivationState;
  })
  .on(activateQrCodeFx.pending, (state, isLoading) => ({ ...state, isLoading }))
  
  .on(activateQrCodeFx.doneData, (state, result) => {
    // Показываем страницу поздравления вместо алерта
    if (!result.success) {     
      window.Telegram?.WebApp?.showAlert(result.message || 'Ошибка');
    }
    
    return { 
      ...state,
      success: result.success,
      message: result.message,
      qrCode: result.success ? '' : state.qrCode, // Очищаем QR-код при успешной активации
      rewardId: result.rewardId,
      showRewardCongratulations: result.success
    } as ActivationState;
  })
  .on(activateQrCodeFx.failData, (state, error) => {
    console.log('Activation failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    console.log('Previous state isSessionExpired:', state.isSessionExpired);
    window.Telegram?.WebApp?.showAlert(error.message);
    const newState = { 
      ...state, 
      message: error.message,
      success: false,
      rewardId: null,
      isSessionExpired: error.isSessionExpired || false
    };
    console.log('New state isSessionExpired:', newState.isSessionExpired);
    return newState;
  });

// Actions
export const activateQrCode = createEvent<{
  code: string;
  telegramId: string;
  username: string;
  phone: string;
  displayName: string;
  initData: string;
}>();

// Добавляем логирование для отслеживания вызовов
activateQrCode.watch((data) => {
  console.log('activateQrCode event triggered with data:', data);
});

// Логика
sample({
  clock: activateQrCode,
  fn: (request) => {
    console.log('Sample function called with request:', request);
    const result = createAuthenticatedRequest({
      code: request.code,
      telegramId: request.telegramId,
      username: request.username,
      phone: request.phone,
      displayName: request.displayName
    }, request.initData);
    console.log('Sample function result:', result);
    return result;
  },
  target: activateQrCodeFx
});


sample({
  clock: activateQrCodeFx.doneData,
  filter: (result: RequestResponse) => result.success,
  fn: () => {
    return true;
  },
  target: setShowRewardCongratulations
});

sample({
  clock: resetActivation, 
  fn: () => 'hidden' as SecondaryButtonState,
  target: setSecondaryButtonState
});
sample({
  clock: resetActivation,
  fn: () => {
    const skipQrScan = $skipQrScan.getState();
    return {
      text: skipQrScan ? 'Активировать код' : 'Сканировать QR-код',
      action: () => (skipQrScan ? activatePendingCode() : scanQRCode()),
      state: 'scan'
    } as MainButtonStore;
  },
  target: setMainButton
});
